import { type ReactElement, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProjectSettingsViewModel } from '@/features/projects/hooks/useProjectSettingsViewModel';
import {
  buildWorkspaceProjectSettingsHref,
  missingProjectRedirectMessage,
  resolveProjectSettingsProjectId,
} from '@/features/projects/utils/projectSettingsRoute';
import { UnauthorizedScreen } from '@/pages/UnauthorizedScreen';
import { getContextMessage } from '@/shared/copy/context';
import { getLoadingMessage } from '@/shared/copy/loading';
import { useOrganisationDetailQuery, useOrganisationEntitlements } from '@/shared/hooks';
import { useSettingsPageLoadingGate } from '@/shared/hooks/useSettingsPageLoadingGate';
import { PageStateScaffold } from '@/ui/composites/scaffolds';
import { SettingsPageLoadingState } from '@/ui/composites/SettingsPageLoadingState';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { ProjectSettingsContent } from './ProjectSettingsContent';
import { ProjectSettingsProjectPicker } from './ProjectSettingsProjectPicker';
import type { AuthSession } from '@/auth/types';
import type {
  ProjectDataWipeSectionProps,
  ProjectTestDataControlsSectionProps,
} from '@/features/projects/components';
import type {
  ProjectApiKeySectionProps,
  ProjectDetailsSectionProps,
} from '@/features/projects/components/ProjectSettingsSections';

type ProjectSettingsPageProps = { session: AuthSession | null; routeProjectId?: string | null };

const projectSettingsSlugPathPattern = /^\/workspaces\/[^/]+\/projects\/[^/]+\/settings$/;

export const ProjectSettingsPage = ({
  session,
  routeProjectId = null,
}: ProjectSettingsPageProps): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setOverrideItems, clearOverride } = useBreadcrumbs();
  const {
    projectQuery,
    project,
    updateProject,
    rotateProjectApiKey,
    deleteProject,
    testProjectPipelineWebhook,
    wipeProjectData,
    selectedOrganisationId,
    selectedProjectId,
    canManageProject,
    canManageProjectLoading,
    nameValue,
    setNameValue,
    nameDirty,
    setNameDirty,
    slugValue,
    setSlugValue,
    badgeEnabledValue,
    setBadgeEnabledValue,
    badgeCharsValue,
    setBadgeCharsValue,
    badgeColorValue,
    setBadgeColorValue,
    apiKeyValue,
    setApiKeyValue,
    revokeOpen,
    setRevokeOpen,
    mutationError,
    apiKeyError,
    suiteThresholdDrafts,
    setSuiteThresholdDrafts,
    suitePreferredUnitsRef,
    resetSuiteThresholdDrafts,
    suiteThresholdsDirty,
    suiteThresholdsValid,
    testThresholdDrafts,
    setTestThresholdDrafts,
    testPreferredUnitsRef,
    resetTestThresholdDrafts,
    testThresholdsDirty,
    testThresholdsValid,
    executionRunThresholdDrafts,
    setExecutionRunThresholdDrafts,
    executionRunPreferredUnitsRef,
    resetExecutionRunThresholdDrafts,
    executionRunThresholdsDirty,
    executionRunThresholdsValid,
    pipelineThresholdDrafts,
    setPipelineThresholdDrafts,
    pipelinePreferredUnitsRef,
    resetPipelineThresholdDrafts,
    pipelineThresholdsDirty,
    pipelineThresholdsValid,
    stageThresholdDrafts,
    setStageThresholdDrafts,
    stagePreferredUnitsRef,
    resetStageThresholdDrafts,
    stageThresholdsDirty,
    stageThresholdsValid,
    jobThresholdDrafts,
    setJobThresholdDrafts,
    jobPreferredUnitsRef,
    resetJobThresholdDrafts,
    jobThresholdsDirty,
    jobThresholdsValid,
    notify,
  } = useProjectSettingsViewModel(session, routeProjectId);
  const resolvedProject = project ?? null;
  const entitlementsQuery = useOrganisationEntitlements(resolvedProject?.organisationId ?? null);
  const organisationQuery = useOrganisationDetailQuery(resolvedProject?.organisationId ?? null);
  const entitlements = entitlementsQuery.data ?? null;
  const isFreePlan = entitlements?.plan.code === 'trial';
  const hideDeliverySettings = entitlements !== null && !entitlements.addOns.deliveryEnabled;
  const hideCodeSettings = entitlements !== null && !entitlements.addOns.codeEnabled;
  const lockUploadTokenSettings =
    entitlements !== null && (isFreePlan || !entitlements.plan.features.apiUploadsEnabled);
  const lockUploadSettings = isFreePlan;
  const hideUploadTokenSettings = false;
  const hidePipelineWebhookSettings =
    entitlements !== null && !entitlements.addOns.operationsEnabled;
  const hidePipelineMetricThresholds =
    entitlements !== null &&
    entitlements.plan.code === 'trial' &&
    !entitlements.addOns.operationsEnabled;
  const isProjectSettingsRoute =
    location.pathname === '/project-settings' ||
    projectSettingsSlugPathPattern.test(location.pathname);
  const searchProjectId = resolveProjectSettingsProjectId(location.search);
  const hasProjectSettingsTarget = selectedProjectId !== null;
  const projectInitialLoadResolved =
    !hasProjectSettingsTarget || resolvedProject !== null || projectQuery.isFetched;
  const settingsPageRouteKey = `${location.pathname}?projectId=${
    routeProjectId ?? searchProjectId ?? selectedProjectId ?? ''
  }`;
  const isLoading = useSettingsPageLoadingGate(
    settingsPageRouteKey,
    projectInitialLoadResolved && !canManageProjectLoading,
  );
  const canonicalWorkspaceSlug = organisationQuery.data?.slug ?? null;
  const canonicalProjectSettingsHref =
    resolvedProject?.organisationId && canonicalWorkspaceSlug
      ? buildWorkspaceProjectSettingsHref({
          workspaceSlug: canonicalWorkspaceSlug,
          projectSlug: resolvedProject.slug,
        })
      : null;

  useEffect(() => {
    if (isProjectSettingsRoute) return;
    clearOverride();
  }, [clearOverride, isProjectSettingsRoute]);

  useEffect(() => {
    if (location.pathname !== '/project-settings') return;
    if (canonicalProjectSettingsHref === null) return;
    void navigate(`${canonicalProjectSettingsHref}${location.hash}`, { replace: true });
  }, [canonicalProjectSettingsHref, location.hash, location.pathname, navigate]);

  useEffect(() => {
    if (!isProjectSettingsRoute) return;
    if (!resolvedProject) return;
    setOverrideItems([
      { label: 'Projects', href: '/projects' },
      { label: resolvedProject.name, href: `/projects/${resolvedProject.slug}` },
      { label: 'Settings' },
    ]);
  }, [isProjectSettingsRoute, resolvedProject, setOverrideItems]);

  useEffect(
    () => () => {
      clearOverride();
    },
    [clearOverride],
  );

  useEffect(() => {
    if (!isProjectSettingsRoute) return;
    const handleOrgRemoved = (): void => {
      void navigate('/projects', {
        replace: true,
        state: { notice: 'You no longer have access to that workspace' },
      });
    };

    window.addEventListener('flowqa-org-removed', handleOrgRemoved);
    return () => {
      window.removeEventListener('flowqa-org-removed', handleOrgRemoved);
    };
  }, [isProjectSettingsRoute, navigate]);

  if (!isProjectSettingsRoute) {
    return <></>;
  }

  if (selectedProjectId === null && routeProjectId === null && searchProjectId === null) {
    return (
      <PageStateScaffold
        session={session}
        sessionMessage="Sign in to manage project settings."
        organisationId={selectedOrganisationId}
        requireOrganisation
        organisationMessage={getContextMessage('selection.workspace.manageSettings')}
      >
        <ProjectSettingsProjectPicker selectedOrganisationId={selectedOrganisationId} />
      </PageStateScaffold>
    );
  }

  const projectDetailsSection: ProjectDetailsSectionProps = {
    project: resolvedProject,
    nameValue,
    setNameValue,
    nameDirty,
    setNameDirty,
    slugValue,
    setSlugValue,
    badgeEnabledValue,
    setBadgeEnabledValue,
    badgeCharsValue,
    setBadgeCharsValue,
    badgeColorValue,
    setBadgeColorValue,
    updateProject,
    mutationError,
    notify,
  };

  const projectApiKeySection: ProjectApiKeySectionProps = {
    project: resolvedProject,
    apiKeyValue,
    setApiKeyValue,
    revokeOpen,
    setRevokeOpen,
    updateProject,
    rotateProjectApiKey,
    testProjectPipelineWebhook,
    apiKeyError,
    notify,
  };

  const projectTestDataControlsSection: ProjectTestDataControlsSectionProps = {
    project: resolvedProject,
    updateProject,
    mutationError,
    notify,
  };

  const projectDataWipeSection: ProjectDataWipeSectionProps = {
    project: resolvedProject,
    deleteProject,
    wipeProjectData,
    mutationError,
    notify,
    onProjectDeleted: () => {
      void navigate('/projects', {
        replace: true,
        state: { notice: 'Project deleted' },
      });
    },
  };

  const viewModel = {
    projectQuery,
    project,
    updateProject,
    mutationError,
    selectedOrganisationId,
    selectedProjectId,
    canManageProject,
    canManageProjectLoading,
    suiteThresholdDrafts,
    setSuiteThresholdDrafts,
    suitePreferredUnitsRef,
    resetSuiteThresholdDrafts,
    suiteThresholdsDirty,
    suiteThresholdsValid,
    testThresholdDrafts,
    setTestThresholdDrafts,
    testPreferredUnitsRef,
    resetTestThresholdDrafts,
    testThresholdsDirty,
    testThresholdsValid,
    executionRunThresholdDrafts,
    setExecutionRunThresholdDrafts,
    executionRunPreferredUnitsRef,
    resetExecutionRunThresholdDrafts,
    executionRunThresholdsDirty,
    executionRunThresholdsValid,
    pipelineThresholdDrafts,
    setPipelineThresholdDrafts,
    pipelinePreferredUnitsRef,
    resetPipelineThresholdDrafts,
    pipelineThresholdsDirty,
    pipelineThresholdsValid,
    stageThresholdDrafts,
    setStageThresholdDrafts,
    stagePreferredUnitsRef,
    resetStageThresholdDrafts,
    stageThresholdsDirty,
    stageThresholdsValid,
    jobThresholdDrafts,
    setJobThresholdDrafts,
    jobPreferredUnitsRef,
    resetJobThresholdDrafts,
    jobThresholdsDirty,
    jobThresholdsValid,
    notify,
    navigate,
  };
  return (
    <PageStateScaffold
      session={session}
      sessionMessage="Sign in to manage project settings."
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      requireProject
      projectMessage={getContextMessage('selection.project.manageSettings')}
      requireAdmin
      isAdmin={canManageProject}
      adminFallback={
        <UnauthorizedScreen
          title="Unauthorised"
          body="You must be an admin to manage project settings."
          backTo="/projects"
          backLabel="Back to projects"
        />
      }
      redirecting={projectQuery.isFetched && resolvedProject === null}
      redirectMessage={missingProjectRedirectMessage}
      isLoading={isLoading}
      loadingFallback={<SettingsPageLoadingState message={getLoadingMessage('projectSettings')} />}
      isError={Boolean(projectQuery.error)}
      error={projectQuery.error}
    >
      <ProjectSettingsContent
        viewModel={viewModel}
        projectDetailsSection={projectDetailsSection}
        projectApiKeySection={projectApiKeySection}
        projectTestDataControlsSection={projectTestDataControlsSection}
        projectDataWipeSection={projectDataWipeSection}
        hideCodeSettings={hideCodeSettings}
        hideDeliverySettings={hideDeliverySettings}
        hideUploadTokenSettings={hideUploadTokenSettings}
        lockUploadSettings={lockUploadSettings}
        lockUploadTokenSettings={lockUploadTokenSettings}
        hidePipelineWebhookSettings={hidePipelineWebhookSettings}
        hidePipelineMetricThresholds={hidePipelineMetricThresholds}
        lockMetricThresholds={isFreePlan}
      />
    </PageStateScaffold>
  );
};
